import React from 'react';

import { useListBox, useListBoxSection, useOption } from '@react-aria/listbox';
import type { AriaListBoxOptions } from '@react-aria/listbox';

import type { ListState } from '@react-stately/list';

import type { Node } from '@react-types/shared';

interface ListBoxProps<T extends unknown> extends AriaListBoxOptions<T> {
  listBoxRef?: React.RefObject<HTMLUListElement>;
  state: ListState<T>;
}

interface SectionProps<T extends unknown> {
  section: Node<T>;
  state: ListState<T>;
}

interface OptionProps<T extends unknown> {
  item: Node<T>;
  state: ListState<T>;
}

const ListBoxAria = <T,>(props: ListBoxProps<T>) => {
  let ref = React.useRef<HTMLUListElement>(null);
  let { listBoxRef = ref, state } = props;
  let { listBoxProps, labelProps } = useListBox(props, state as any, listBoxRef);

  return (
    <>
      <div {...labelProps}>{props.labelList}</div>
      <ul {...listBoxProps} ref={listBoxRef} className="max-h-72 overflow-auto outline-none">
        {[...state.collection].map((item) =>
          item.type === 'section' ? (
            <ListBoxSection key={item.key} section={item} state={state} />
          ) : (
            <Option key={item.key} item={item} state={state} />
          )
        )}
      </ul>
    </>
  );
};

const ListBoxSection = <T,>({ section, state }: SectionProps<T>) => {
  let { itemProps, headingProps, groupProps } = useListBoxSection({
    heading: section.rendered,
    'aria-label': section['aria-label']
  });

  return (
    <>
      <li {...itemProps} className="pt-2">
        {section.rendered && (
          <span {...headingProps} className="mx-3 text-xs font-bold uppercase text-gray-500">
            {section.rendered}
          </span>
        )}
        <ul {...groupProps}>
          {[...section.childNodes].map((node) => (
            <Option key={node.key} item={node} state={state} />
          ))}
        </ul>
      </li>
    </>
  );
};

const Option = <T,>({ item, state }: OptionProps<T>) => {
  let ref = React.useRef<HTMLLIElement>(null);
  let { optionProps, isDisabled, isSelected, isFocused } = useOption(
    {
      key: item.key
    },
    state as any,
    ref
  );

  let text = 'text-gray-700';
  if (isFocused || isSelected) {
    text = 'text-gray-600';
  } else if (isDisabled) {
    text = 'text-gray-200';
  }

  return (
    <li
      {...optionProps}
      ref={ref}
      className={`m-1 flex cursor-default items-center justify-between rounded-md py-2 px-2 text-left text-sm outline-none ${text} ${
        isFocused ? 'bg-gray-100' : ''
      } ${isSelected ? 'font-bold' : ''}`}
    >
      {item.rendered}
      {/* {isSelected && <FontAwesomeIcon className={text} icon={faBadgeCheck} />} */}
    </li>
  );
};

export default ListBoxAria;
